// src/pages/ChangePassword.jsx
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Button from "../components/Button";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../api/api";
import Loading from "../components/Loading";
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TokenService from '../services/tokenService';
import { BiCheck, BiX, BiShow, BiHide } from 'react-icons/bi';
import { validatePassword, calculatePasswordStrength } from '../lib/validationUtils';

const ChangePassword = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { passwordChangeRequired } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasErrors, setHasErrors] = useState(false);
  
  const [formData, setFormData] = useState({
    username: "",
    newPassword: "",
    confirmPassword: "",
    token: "",
    preferredLanguage: 'en',
    sendResetEmail: false,
    sendChangePassword: false
  });

  useEffect(() => {
    // Prevent back navigation if password change is required
    const handleBackButton = (e) => {
      if (passwordChangeRequired) {
        e.preventDefault();
        history.push('/changePassword');
      }
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [passwordChangeRequired, history]);

  useEffect(() => {
    // Handle both scenarios
    const searchParams = new URLSearchParams(location.search);
    const urlToken = searchParams.get("code");
    const { firstTimeLogin, loginToken } = location.state || {};

    // Get current language dynamically
    const currentLanguage = i18n.language || 'en';

    if (firstTimeLogin && loginToken) {
      // First time login scenario
      setFormData(prev => ({ 
        ...prev, 
        token: loginToken, 
        preferredLanguage: currentLanguage, // Use dynamic language
        sendResetEmail: false, 
        sendChangePassword: true 
      }));
    } else if (urlToken) {
      // Email reset scenario
      setFormData(prev => ({ 
        ...prev, 
        token: urlToken, 
        preferredLanguage: currentLanguage, // Use dynamic language
        sendResetEmail: true, 
        sendChangePassword: false 
      }));
    } else if (!firstTimeLogin) {
      // Only show error if it's not first time login
      setErrorMsg(t("changePassword.invalidLink"));
    }
}, [location, t, i18n]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Clear errors when user starts typing
    if (errors[id]) {
      const newErrors = { ...errors };
      delete newErrors[id];
      setErrors(newErrors);
      setHasErrors(Object.keys(newErrors).length > 0);
    }

    // Clear error message when user starts typing
    if (errorMsg) {
      setErrorMsg("");
    }

    // Calculate password strength in real-time for new password
    if (id === 'newPassword') {
      if (value) {
        setPasswordStrength(calculatePasswordStrength(value));
      } else {
        setPasswordStrength(null);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username) {
      newErrors.username = true;
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = true;
    } else {
      const passwordValidation = validatePassword(formData.newPassword);
      if (!passwordValidation.isValid) {
        newErrors.newPassword = true;
      }
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = true;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = true;
    }

    if (!formData.token) {
      newErrors.token = true;
    }

    setErrors(newErrors);
    const hasValidationErrors = Object.keys(newErrors).length > 0;
    setHasErrors(hasValidationErrors);
    return !hasValidationErrors;
  };

  // Function to translate API error messages
  const translateApiError = (errorMessage) => {
    if (!errorMessage) return t("changePassword.error");
    
    const lowerError = errorMessage.toLowerCase();
    
    // Check for specific API error messages
    if (lowerError.includes('password has been used before') || 
        lowerError.includes('password already used') ||
        lowerError.includes('previously used password')) {
      return t('changePassword.passwordUsedBefore');
    }

    if (lowerError.includes('User not found') ||
        lowerError.includes('user not found') ||
        lowerError.includes('user does not exist')) {
      return t('changePassword.userNotFound');
    }
    
    if (lowerError.includes('invalid or expired') || 
        lowerError.includes('expired') || 
        lowerError.includes('invalid link') ||
        lowerError.includes('invalid token')) {
      return t('changePassword.invalidOrExpiredLink');
    }
    
    // Check for password mismatch from API
    if (lowerError.includes('passwords do not match') || 
        lowerError.includes('password mismatch')) {
      return t('changePassword.passwordMismatch');
    }
    
    // Check for required fields from API
    if (lowerError.includes('required') || 
        lowerError.includes('missing')) {
      return t('changePassword.allFieldsRequired');
    }
    
    // Default error for unrecognized messages
    return errorMessage;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await resetPassword(
        formData.username,
        formData.newPassword,
        formData.confirmPassword,
        formData.token,
        {
          preferredLanguage: formData.preferredLanguage,
          sendResetEmail: formData.sendResetEmail,
          sendChangePassword: formData.sendChangePassword
        }
      );

      if (response.isSuccess) {
        TokenService.clearAll();
        setErrorMsg(t("changePassword.success"));
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
      } else {
        // Use the translation function for API errors
        const translatedError = translateApiError(response.message);
        setErrorMsg(translatedError);
      }
    } catch (error) {
      console.error('Password change error:', error);
      // Use the translation function for caught errors
      const translatedError = translateApiError(error.message);
      setErrorMsg(translatedError);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'strong': return 'text-blue-500';
      case 'very-strong': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getStrengthBgColor = (strength) => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-blue-500';
      case 'very-strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'weak': return t('signup.password.weak');
      case 'medium': return t('signup.password.medium');
      case 'strong': return t('signup.password.strong');
      case 'very-strong': return t('signup.password.veryStrong');
      default: return '';
    }
  };

  const getErrorMessage = () => {
    if (!formData.username && !formData.newPassword && !formData.confirmPassword) {
      return t('changePassword.allFieldsRequired');
    }
    
    if (!formData.username) {
      return t('changePassword.allFieldsRequired');
    }
    
    if (!formData.newPassword) {
      return t('changePassword.allFieldsRequired');
    }
    
    if (formData.newPassword && !validatePassword(formData.newPassword).isValid) {
      return t('signup.errors.passwordRequirements');
    }
    
    if (!formData.confirmPassword) {
      return t('changePassword.allFieldsRequired');
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      return t('changePassword.passwordMismatch');
    }
    
    return '';
  };

  const getDisplayMessage = () => {
    if (hasErrors) {
      return getErrorMessage();
    }
    
    return t('signup.password.requiredFieldsMessage');
  };

  return (
    <section className="h-full w-full flex justify-center items-center p-5">
      <form className="border-2 p-10 rounded-xl flex flex-col gap-5 max-w-2xl bg-zinc-100 justify-center items-start">
        <div className="w-full flex flex-col justify-center items-center mb-5">
          <h2 className="text-xl text-center font-semibold">
            {t("changePassword.submit")}
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            {t('signup.password.instructions')}
          </p>
        </div>

        {/* Username Field */}
        <div className="w-full flex flex-col gap-2">
          <label htmlFor="username" className="labels block text-sm font-semibold">
            {t("signin.Username")} *
          </label>
          <input
            onChange={handleChange}
            value={formData.username}
            autoComplete="off"
            id="username"
            type="text"
            placeholder={t("signin.Username")}
            required
            className={`min-w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.username ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={loading}
          />
        </div>

        {/* New Password Field */}
        <div className="w-full flex flex-col gap-2">
          <label htmlFor="newPassword" className="labels block text-sm font-semibold">
            {t("changePassword.newPassword")} *
          </label>
          <div className="relative">
            <input
              onChange={handleChange}
              value={formData.newPassword}
              autoComplete="new-password"
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder={t("changePassword.newPassword")}
              required
              className={`min-w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.newPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white p-1 rounded text-gray-500 hover:text-gray-700"
              disabled={loading}
            >
              {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
            </button>
          </div>
        </div>

        {/* Password Strength Indicator */}
        {passwordStrength && (
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('signup.password.passwordStrength')}</span>
              <span className={`font-medium capitalize ${getStrengthColor(passwordStrength.strength)}`}>
                {getStrengthText(passwordStrength.strength)}
              </span>
            </div>
            
            {/* Strength Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getStrengthBgColor(passwordStrength.strength)}`}
                style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
              />
            </div>

            {/* Requirements Checklist */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-500 mb-2">{t('signup.password.passwordRequirements')}</p>
              <div className="space-y-1 text-xs">
                <div className={`flex items-center ${passwordStrength.checks.length ? 'text-green-500' : 'text-gray-400'}`}>
                  {passwordStrength.checks.length ? <BiCheck size={14} /> : <BiX size={14} />}
                  <span className="ml-1">{t('signup.password.characters')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.uppercase ? 'text-green-500' : 'text-gray-400'}`}>
                  {passwordStrength.checks.uppercase ? <BiCheck size={14} /> : <BiX size={14} />}
                  <span className="ml-1">{t('signup.password.uppercase')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.lowercase ? 'text-green-500' : 'text-gray-400'}`}>
                  {passwordStrength.checks.lowercase ? <BiCheck size={14} /> : <BiX size={14} />}
                  <span className="ml-1">{t('signup.password.lowercase')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.number ? 'text-green-500' : 'text-gray-400'}`}>
                  {passwordStrength.checks.number ? <BiCheck size={14} /> : <BiX size={14} />}
                  <span className="ml-1">{t('signup.password.number')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.symbol ? 'text-green-500' : 'text-gray-400'}`}>
                  {passwordStrength.checks.symbol ? <BiCheck size={14} /> : <BiX size={14} />}
                  <span className="ml-1">{t('signup.password.symbol')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Password Field */}
        <div className="w-full flex flex-col gap-2">
          <label htmlFor="confirmPassword" className="labels block text-sm font-semibold">
            {t("changePassword.confirmPassword")} *
          </label>
          <div className="relative">
            <input
              onChange={handleChange}
              value={formData.confirmPassword}
              autoComplete="new-password"
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t("changePassword.confirmPassword")}
              required
              className={`min-w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white p-1 rounded text-gray-500 hover:text-gray-700"
              disabled={loading}
            >
              {showConfirmPassword ? <BiHide size={20} /> : <BiShow size={20} />}
            </button>
          </div>
          
          {/* Password Match Indicator - Only show if both fields have values */}
          {formData.newPassword && formData.confirmPassword && (
            <div className={`flex items-center mt-1 text-xs ${
              formData.newPassword === formData.confirmPassword ? 'text-green-600' : 'text-red-500'
            }`}>
              {formData.newPassword === formData.confirmPassword ? <BiCheck size={16} /> : <BiX size={16} />}
              <span className="ml-1">
                {formData.newPassword === formData.confirmPassword 
                  ? t('signup.password.passwordsMatch') 
                  : t('signup.password.passwordsDoNotMatch')
                }
              </span>
            </div>
          )}
        </div>

        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex justify-center items-center mt-4">
            {!loading ? (
              <Button 
                type="submit" 
                onClick={handleSubmit} 
                variant="block"
                label={t("changePassword.submit")} 
              />
            ) : (
              <Loading />
            )}
          </div>
        </div>

        {/* Helper Text - Show error message or default message */}
        <div className="w-full text-center">
          <p className={`text-xs ${hasErrors ? 'text-red-500' : 'text-gray-500'}`}>
            {getDisplayMessage()}
          </p>
        </div>

        {errorMsg && (
          <span className={`w-full text-center p-2 ${
            errorMsg === t("changePassword.success") 
              ? "text-green-600" 
              : "text-red-500"
          }`}>
            {errorMsg}
          </span>
        )}
      </form>
    </section>
  );
};

export default ChangePassword;